import {
  allSectionsOf,
  evaluateConditions,
  getInstanceCount,
  instanceCountKey,
  qualifiedKey,
  renderInstanceLabel,
  resolveColumnLabel,
  scopeValuesToInstance,
  sectionMinInstances,
  unitModeKey,
} from '@bmo/form-core';

import { isLayoutBlockType, type WorkflowField, type WorkflowTemplate } from '@/api/workflows';

import { type FieldValue } from './FieldRenderer';
import { hiddenBorrowedFieldIds } from './hiddenReveals';

/**
 * One answered field, reduced to just its originator-authored label and raw
 * value. Keyed by the field's stable `id` in {@link ApplicationSnapshot.plctData},
 * so the snapshot round-trips back into FormShell's `initialValues`: restore each
 * `[fieldId, value]` pair (see {@link restoreValuesFromSnapshot}).
 */
export type SnapshotFieldEntry = {
  /** Field's display label in the snapshot language (markdown bold markers
   *  stripped), stored purely for human/BMO readability alongside the id key. */
  label: string;
  value: FieldValue;
};

/**
 * The application snapshot as saved to BMO. `plctData` is a flat map of field id
 * (UUID) to its `{ label, value }`, across every currently-visible ANSWERED
 * field in the whole application. It carries no step/section structure — field
 * ids are global UUIDs, so this map alone fully rehydrates FormShell's answer
 * state; an absent key restores as an unanswered field, which is what it was.
 *
 * Two kinds of key are NOT a bare field id, because two answers are not fields: a field
 * inside a repeatable section is keyed `inst:<sectionId>:<n>:<fieldId>`, and a dual-unit
 * column's "$ or %" mode is keyed `unitMode:<fieldId>:<columnId>` (see
 * {@link collectUnitModes}). Both round-trip through this map like any other entry.
 */
export type ApplicationSnapshot = {
  /**
   * The application's language — what BMO stores as `appInfo.appLanguage`, and the locale every
   * `plctData` label below was rendered in.
   *
   * It rides on the snapshot rather than being read separately at the save call site so the two
   * can never disagree: the caller that sends BMO these labels sends the language they are in.
   * The value is the CLIENT's language (the Welcome step's radio when the workflow has one,
   * otherwise the advisor's browser/header locale) — never the advisor's UI locale on its own.
   */
  language: 'en' | 'fr';
  plctData: Record<string, SnapshotFieldEntry>;
};

/**
 * Serialize FormShell's live answer map into a flat snapshot of the whole
 * application. Walks every DynamicForm step (only those carry answers; other
 * step types and originator-only PdfFileUpload steps are skipped) and records
 * each currently-visible, ANSWERED input field as `fieldId -> { label, value }`,
 * applying the same step/section/field visibility gating and layout-block
 * skipping as the Application Review recap so the snapshot mirrors exactly what
 * would be submitted.
 *
 * A visible field the advisor has not answered is omitted rather than sent with
 * a null value — see the check at the write site. Falsy answers (`0`, `false`,
 * `''`) are answers and are kept.
 */
export function buildApplicationSnapshot(
  template: WorkflowTemplate,
  values: Record<string, FieldValue>,
  language: 'en' | 'fr',
): ApplicationSnapshot {
  const isFr = language === 'fr';
  const plctData: Record<string, SnapshotFieldEntry> = {};
  const allSections = allSectionsOf(template);

  for (const step of template.steps) {
    // Only form steps carry answers; other steps (and originator-only
    // PdfFileUpload config) contribute nothing to restore.
    if (step.type !== 'DynamicForm') continue;
    // Skip a whole step when its own visibility rule currently hides it.
    if (!isVisible(step.conditions, step.conditionCombinator, values)) continue;
    // Reveal targets borrowed by an UNSELECTED RadioGroup / CheckboxGroup
    // option render nowhere, but they carry no conditions of their own (the
    // owning option owns their visibility), so the condition checks below
    // can't catch them. Without this a value typed while an option was
    // selected would keep appearing on the review after the advisor changed
    // their answer.
    const hiddenBorrowed = hiddenBorrowedFieldIds(step, values);

    for (const section of step.sections) {
      // Skip a section when its visibility rule currently hides it.
      if (!isVisible(section.conditions, section.conditionCombinator, values)) continue;

      // A repeatable section keeps one answer PER INSTANCE, under `inst:<section>:<n>:<field>`.
      // This walk used to read only the bare field id, so no repeatable answer was ever saved —
      // on RDSP that is every account holder (Accountholder 1 included), the relationship blocks
      // and the caregivers — and a resumed application came back with them blank. Each instance
      // is judged visible on its OWN answers, exactly as `visibleValues` and the form do.
      const repeatable = section.repeatable === true;
      const instanceCount = repeatable
        ? getInstanceCount(values, section.id, sectionMinInstances(section))
        : 1;

      for (const row of section.rows) {
        for (const col of row.columns) {
          const f = col.field;
          if (!f) continue;
          // Layout blocks (StaticText / Divider / Notice / …) hold no answer.
          if (isLayoutBlockType(f.type)) continue;
          // …or hidden by the option that borrows it not being ticked.
          if (hiddenBorrowed.has(f.id)) continue;
          const baseLabel = pickText(f.labelEn, f.labelFr, isFr).replace(/\*\*/g, '').trim();

          for (let i = 0; i < instanceCount; i++) {
            const scoped = repeatable
              ? scopeValuesToInstance(section, values, i, allSections)
              : values;
            // Skip a field hidden by its own conditions.
            if (!isVisible(f.conditions, f.conditionCombinator, scoped)) continue;
            const key = repeatable ? qualifiedKey(section.id, i, f.id) : f.id;

            // A dual-unit column's "$ or %" mode, which is an answer the advisor gave but
            // no FIELD holds — see the walk below. Emitted once per field rather than once
            // per instance because the key is composed from the bare field id: the toggle
            // governs the whole column, exactly as `visibleValues` treats it.
            if (i === 0) collectUnitModes(plctData, f, values, baseLabel, language);

            // An unanswered field is OMITTED, not sent as `{ value: null }`. The
            // advisor reaches the e-signature step through a dozen saves, and a
            // null-valued entry says "this field exists and holds nothing" where
            // an absent key says "not answered yet" — the same thing to us, but
            // only the second is a fact BMO has to store. Safe against the full-
            // state contract (ADR-0010 / A12) because every save rebuilds the map
            // from scratch: a field the advisor clears drops out of the next save
            // rather than lingering from an earlier one.
            const value = values[key];
            if (value === null || value === undefined) continue;

            plctData[key] = {
              // Name the instance, so a reader of the saved data can tell the two holders apart.
              label: repeatable
                ? `${baseLabel} (${renderInstanceLabel(section, i, language)})`
                : baseLabel,
              value,
            };
          }
        }
      }
    }
  }

  return { language, plctData };
}

/**
 * Save the "$ or %" mode of every dual-unit column on a visible RepeatableRow.
 *
 * ## Why this is not covered by the field walk
 *
 * The walk above records FIELDS, and the mode is not one: it lives beside them under
 * `unitMode:<fieldId>:<columnId>` (see `unitModeKey`), written by the column's toggle and
 * read back by `collectFilledFields` to decide which AcroForm box a cell belongs in — the
 * split `%Amount1` / `$Amount1` pair on the BMO transfer forms — and to tick the "$ or %"
 * radio on the application form. Left out of the snapshot it was simply lost on resume, and
 * `readUnitMode` fell back to the column's authored `defaultMode`: an application filled in
 * PERCENT came back, and printed, in DOLLARS. Silently, and on a document someone signs.
 *
 * `restoreValuesFromSnapshot` needs no counterpart — it copies every `plctData` entry back
 * verbatim, so landing the key here is the whole round trip. That makes this the sibling of
 * the instance-count rebuild it does by hand: two bits of bookkeeping that are not fields but
 * are still part of the answer.
 *
 * ## Only a mode the advisor actually set
 *
 * An absent key is written as absent, following the same rule as an unanswered field: both
 * sides of the round trip degrade to `defaultMode`, so inventing an entry would say the
 * advisor chose what they never touched. A column inside a REPEATABLE section is skipped by
 * the same test — the renderer qualifies its writes per instance there, so the bare key this
 * reads is legitimately never set.
 */
function collectUnitModes(
  plctData: Record<string, SnapshotFieldEntry>,
  field: WorkflowField,
  values: Record<string, FieldValue>,
  baseLabel: string,
  language: 'en' | 'fr',
) {
  if (field.type !== 'RepeatableRow') return;
  for (const col of field.repeatableTemplate ?? []) {
    if (!col.unitToggle) continue;
    const key = unitModeKey(field.id, col.id);
    const value = values[key];
    if (value !== 'dollar' && value !== 'percent') continue;
    const colLabel = resolveColumnLabel(col, language, value);
    plctData[key] = { label: `${baseLabel} — ${colLabel} ($ or %)`, value };
  }
}

/**
 * Inverse of {@link buildApplicationSnapshot}: rebuild FormShell's flat answer
 * map from a saved snapshot, ready to feed straight into the `initialValues`
 * prop. Restore needs only the `[fieldId, value]` pairs — labels come from the
 * template, and so does the language (the resume path seeds it from the
 * template's own Welcome radio, not from the values), which is why the
 * parameter asks for `plctData` alone rather than a whole snapshot.
 *
 * NOT the last word on advisor-sourced fields. What comes back here for a
 * `sourceValue: 'advisor.*'` slot or an `advisorRepCodeSelector` is the advisor
 * identity as it stood at the LAST SAVE, and identity is re-resolved from the
 * live `/me` session once FormShell mounts — see `decideAdvisorPrefill` in
 * advisorPrefill.ts and ADR-0010. Restoring them anyway is deliberate: they
 * seed the form for the tick before the session lands, and the session-prefill
 * pass never blanks a slot it has nothing current to say about.
 */
export function restoreValuesFromSnapshot(
  snapshot: Pick<ApplicationSnapshot, 'plctData'>,
): Record<string, FieldValue> {
  const values: Record<string, FieldValue> = Object.fromEntries(
    Object.entries(snapshot.plctData).map(([fieldId, entry]) => [fieldId, entry.value]),
  );
  // A repeatable section's instance count is not saved — BMO has no use for our bookkeeping —
  // so it is rebuilt from the highest instance that came back with an answer. Without it a
  // resumed application would show Accountholder 2's answers under a single Accountholder.
  const counts = new Map<string, number>();
  for (const key of Object.keys(values)) {
    const m = INSTANCE_KEY.exec(key);
    if (!m) continue;
    const n = Number(m[2]) + 1;
    if (n > (counts.get(m[1]!) ?? 0)) counts.set(m[1]!, n);
  }
  for (const [sectionId, n] of counts) values[instanceCountKey(sectionId)] = n;
  return values;
}

/** `inst:<sectionId>:<index>:<fieldId>` — see `qualifiedKey`. */
const INSTANCE_KEY = /^inst:([^:]+):(\d+):[^:]+$/;

/** Whether a conditions block is currently visible. Thin alias over
 *  {@link evaluateConditions} (which already treats empty/absent conditions as
 *  visible) that reads clearly at the step/section/field call sites. */
function isVisible(
  conditions: Parameters<typeof evaluateConditions>[0],
  combinator: Parameters<typeof evaluateConditions>[1],
  values: Record<string, FieldValue>,
): boolean {
  return evaluateConditions(conditions, combinator, values);
}

/** Language pick with a fallback to the other language when the preferred side
 *  is blank — mirrors the `(isFr ? fr : en) || en || fr` idiom used across the
 *  renderer. */
function pickText(en: string, fr: string, isFr: boolean): string {
  return (isFr ? fr : en) || en || fr || '';
}
